import { Ctor } from '../types';
import { Credential } from './credential';
import { UnknownDraftCredential } from './draft/unknown';
import { Claims } from './types';
import { UnknownVerifiableCredential } from './verifiable/unknown';
import { CredentialWithClaims } from './with-claims';

type EnvelopedCredential<T extends Claims | unknown> = UnknownDraftCredential<T> | UnknownVerifiableCredential<T>;

export class UnknownCredential<T extends Claims | unknown = unknown> extends Credential<EnvelopedCredential<T>> {
    constructor(protected credential: EnvelopedCredential<T>) {
        super(credential);
    }

    canMapToDraft(): this is Credential<UnknownDraftCredential<T>> {
        return this.credential instanceof UnknownDraftCredential;
    }

    canMapToVerifiableCredential(): this is Credential<UnknownVerifiableCredential<T>> {
        return this.credential instanceof UnknownVerifiableCredential;
    }

    toDraft(): UnknownDraftCredential<T> {
        if (this.canMapToDraft()) {
            return this.credential;
        }
        throw new Error("UnknownCredential can't be mapped to UnknownDraftCredential");
    }

    toVerifiableCredential(): UnknownVerifiableCredential<T> {
        if (this.canMapToVerifiableCredential()) {
            return this.credential;
        }
        throw new Error("UnknownCredential can't be mapped to UnknownVerifiableCredential");
    }

    async canMap<K extends Claims>(
        claimsModel: Ctor<T extends Claims ? (K extends T ? K : never) : K>,
    ): Promise<boolean> {
        return this.credential.canMap(claimsModel);
    }

    async map<K extends Claims>(claimsModel: Ctor<T extends Claims ? (K extends T ? K : never) : K>) {
        return new CredentialWithClaims(await this.credential.map(claimsModel));
    }
}
